import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button, Input } from "@components/ui";
import { getErrorMessage } from "@services/api";
import { authService } from "@services/authService";
import { useAuthStore } from "@store/authStore";

const loginSchema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      login(data.user, data.tokens);
      toast.success("Login successful!");

      // Redirect based on user role
      if (data.user.role === "teacher" || data.user.role === "admin") {
        navigate("/teacher");
      } else {
        navigate("/dashboard");
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(data);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-earth-800 mb-2 font-display">
        Welcome back!
      </h2>
      <p className="text-earth-700 mb-8">
        Enter your credentials to access the portal.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Input
          label="Email"
          type="email"
          placeholder="your@email.com"
          error={errors.email?.message}
          {...register("email")}
        />

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            error={errors.password?.message}
            {...register("password")}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[2.0rem] text-earth-400 hover:text-earth-600 transition-colors focus:outline-none"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <i className="ri-eye-off-line text-xl"></i>
            ) : (
              <i className="ri-eye-line text-xl"></i>
            )}
          </button>
        </div>

        <Button
          type="submit"
          className="w-full bg-olive-600 hover:bg-olive-700 mt-6"
          size="lg"
          isLoading={loginMutation.isPending}
        >
          Login
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-earth-700">
        Contact your administrator for access or password recovery
      </p>
    </div>
  );
}
